/**
 * Browser entry point.
 *
 * Everything above this line is testable without a browser; this file is the
 * one place that touches `document`, so it stays as close to empty as it can.
 *
 * `StrictMode` is on deliberately even though it double-invokes renders and
 * effects in development. The activity player (Phase 1) will own a timing
 * controller with real side effects, and a double-invoked effect that
 * double-counts a timing segment is exactly the bug that would otherwise be
 * found much later, in data that looks plausible.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router';
import { createApi, redirectToLogin } from './api/client.js';
import { Providers, createQueryClient } from './app/providers.js';
import { installPageMorphs } from './app/page-morph.js';
import { routes } from './app/routes.js';
import '@fontsource/rubik/400.css';
import '@fontsource/rubik/500.css';
import '@fontsource/rubik/600.css';
import '@fontsource/rubik/700.css';
// KaTeX's stylesheet (#362) is imported here, not from a component: the visual spec loads the route table in Node.
import 'katex/dist/katex.min.css';
import './styles/global.css';
// Page stylesheets load after global.css so they win its ties; imported here, not from their components, for the same reason as KaTeX's.
import './styles/settings.css';
import './styles/home.css';
import './styles/homework.css';
import './styles/review.css';
import './styles/feedback.css';
import './styles/progress.css';
import './styles/auth.css';
import './styles/prep.css';
// Page morphs (#648) come last so their view-transition rules win ties with the page stylesheets.
import './styles/morph.css';

const container = document.getElementById('root');
if (container === null) {
  throw new Error('index.html has no #root element to mount into.');
}

const queryClient = createQueryClient();
const router = createBrowserRouter(routes);
installPageMorphs(router, queryClient);

createRoot(container).render(
  <StrictMode>
    <Providers api={createApi({ onUnauthenticated: () => redirectToLogin() })} queryClient={queryClient}>
      <RouterProvider router={router} />
    </Providers>
  </StrictMode>,
);
